import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bookmark, Trash2, X, Play } from "lucide-react";
import { useI18n } from "@/lib/i18n";

/**
 * Modal to browse/manage the current user's saved Marketplace searches.
 * On mobile it renders as a bottom-anchored sheet; on desktop as a centred
 * dialog. Selecting a saved search navigates to /marketplace with the stored
 * filters serialised as query-string params (the page picks them up on mount).
 */
export default function SavedSearchesModal({ open, onClose }) {
  const nav = useNavigate();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/marketplace/saved-searches");
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const remove = async (id) => {
    try {
      await api.delete(`/marketplace/saved-searches/${id}`);
      setItems((prev) => prev.filter((x) => x.saved_search_id !== id));
      toast.success("Ricerca eliminata");
    } catch { toast.error("Impossibile eliminare"); }
  };

  const apply = (ss) => {
    const p = ss.params || {};
    const qs = new URLSearchParams();
    Object.entries(p).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") qs.set(k, String(v));
    });
    nav(`/marketplace?${qs.toString()}`);
    onClose?.();
  };

  const paramSummary = (p) => {
    const bits = [];
    if (p.type) bits.push({ sale: "Vendo", gift: "Regalo", reusable: "Riutilizzabile" }[p.type] || p.type);
    if (p.category) bits.push(p.category);
    if (p.subcategory) bits.push(p.subcategory);
    if (p.item_type) bits.push(p.item_type);
    if (p.province) bits.push(p.province);
    if (p.price_min || p.price_max) bits.push(`€ ${p.price_min ?? "…"}–${p.price_max ?? "…"}`);
    if (p.q) bits.push(`"${p.q}"`);
    return bits.join(" · ") || "Nessun filtro";
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[10000] flex items-end sm:items-center justify-center bg-black/50" data-testid="saved-searches-modal">
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="inline-flex items-center gap-2">
            <Bookmark size={16} className="text-emerald-700" />
            <div className="font-heading text-lg font-bold text-slate-900">{t("my_searches_title")}</div>
          </div>
          <button data-testid="saved-searches-close" onClick={onClose} aria-label="Chiudi" className="p-1 rounded-full hover:bg-slate-100">
            <X size={16} />
          </button>
        </div>
        <div className="p-3 overflow-y-auto flex-1">
          {loading && <div className="text-sm text-slate-500 italic p-4 text-center">Caricamento…</div>}
          {!loading && items.length === 0 && (
            <div className="text-sm text-slate-500 italic p-6 text-center" data-testid="saved-searches-empty">
              Nessuna ricerca salvata. Salva una ricerca dal feed Marketplace per essere avvisato quando compare un nuovo annuncio compatibile.
            </div>
          )}
          {items.map((ss) => (
            <div key={ss.saved_search_id} data-testid={`saved-search-row-${ss.saved_search_id}`} className="border border-slate-200 rounded-xl p-3 mb-2 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 text-sm truncate">{ss.name}</div>
                <div className="text-xs text-slate-600 mt-0.5 truncate">{paramSummary(ss.params)}</div>
              </div>
              <button
                data-testid={`saved-search-apply-${ss.saved_search_id}`}
                onClick={() => apply(ss)}
                className="h-8 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold inline-flex items-center gap-1"
              >
                <Play size={12} /> Apri
              </button>
              <button
                data-testid={`saved-search-delete-${ss.saved_search_id}`}
                onClick={() => remove(ss.saved_search_id)}
        aria-label={t("delete_label")}
                className="h-8 w-8 rounded-full bg-white border border-slate-200 hover:bg-red-50 hover:text-red-700 text-slate-500 inline-flex items-center justify-center"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
